import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import SchoolDetailsModal from '../../components/SchoolDetailsModal';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import {
  ArrowLeft,
  Trophy,
  Users,
  TrendingUp,
  Award,
  Dumbbell,
  Target,
  Loader2,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  ComparisonBarChart,
  DistributionPieChart,
  StatCard,
} from '../../components/analytics';
import { useGetSamagamRegionalGroundsQuery, useGetSamagamNationalGroundsQuery, useGetSamagamSportsByCategoryQuery, useGetSamagamQualityDataQuery, useGetSamagamIndoorOutdoorDataQuery, useGetSamagamTopFacilityKvsQuery } from '../../services/kvsSamagamApi';
// import { getSportsInfraByKPI } from '../../services/api'; // API under development — using mock data
import * as LOOKUP from '../../constants/lookupMaps';

// ─── Filter state type ─────────────────────────────────────────────────────────
interface SportsFilters {
  search: string;
  category: string;   // Indoor | Outdoor | ''
  type: string;       // Standard | Ordinary | ''
  quality: string;    // Clay | RCC | Synthetic | Wooden | Turf | Sinder | ''
  forRegional: string;  // 'yes' | 'no' | ''
  forNational: string;  // 'yes' | 'no' | ''
  quantityMin: string;
  quantityMax: string;
}


// ─── Raw record shape coming from the KPI endpoint ────────────────────────────
interface RawSportRecord {
  sportType: string | number;
  category: string;
  type: string;
  quality: string;
  forRegional: boolean | number;
  forNational: boolean | number;
  quantity: number;
  isPMShree: number;
  schoolID?: number;
  schoolName?: string;
  roName?: string;
  stateName?: string;
}

// ─── Aggregated row for comparison charts / table ─────────────────────────────
interface AggRow {
  category: string;
  pmshriCount: number;
  nonPmshriCount: number;
  total: number;
  pmshriPercentage: number;
}

const CATEGORY_OPTIONS = Object.entries(LOOKUP.SPORT_TYPE).map(([k]) => k);      // Indoor, Outdoor

const QUALITY_OPTIONS  = Object.entries(LOOKUP.SPORT_QUALITY).map(([k]) => k);

export default function SportsInfrastructureAnalysis() {
  const navigate = useNavigate();

  // ── Data state ──────────────────────────────────────────────────────────────
  const [rawRecords, setRawRecords] = useState<RawSportRecord[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [selectedSchool, setSelectedSchool] = useState<{
    schoolID: number;
    schoolName: string;
    infraData?: any;
    tabLabel?: string;
  } | null>(null);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'sportName' | 'category' | 'quality' | 'regional' | 'national'>('sportName');
  const [sortCol, setSortCol]     = useState<'total' | 'pmshri' | 'nonPmshri'>('total');
  const [sortDir, setSortDir]     = useState<'desc' | 'asc'>('desc');

  // ── Fetch Regional Grounds Data ──────────────────────────────────────────────
  const { data: regionalData, isLoading: regionalLoading, error: regionalError } = useGetSamagamRegionalGroundsQuery();
  
  // ── Fetch National Grounds Data ──────────────────────────────────────────────
  const { data: nationalData, isLoading: nationalLoading, error: nationalError } = useGetSamagamNationalGroundsQuery();
  
  // ── Fetch Sports By Category Data ────────────────────────────────────────────
  const { data: sportsByCategoryData, isLoading: sportsLoading, error: sportsError } = useGetSamagamSportsByCategoryQuery();
  
  // ── Fetch Quality Data ────────────────────────────────────────────────────────
  const { data: qualityData, isLoading: qualityLoading, error: qualityError } = useGetSamagamQualityDataQuery();

  // ── Fetch Indoor/Outdoor Data ─────────────────────────────────────────────────
  const { data: indoorOutdoorData, isLoading: indoorOutdoorLoading, error: indoorOutdoorError } = useGetSamagamIndoorOutdoorDataQuery();

  // ── Fetch Top Facility KVS Data ────────────────────────────────────────────────
  const { data: topFacilityData, isLoading: topFacilityLoading, error: topFacilityError } = useGetSamagamTopFacilityKvsQuery();

  // ── Fetch (MOCK — replace with real API when ready) ─────────────────────────
  const fetchData = async () => {
    // no-op, mock data removed
    setLoading(false);
    setError(null);
    setRawRecords([]);
  };

  useEffect(() => { fetchData(); }, []); // kept for compatibility (data now empty)

  // ── Filtered records (no filters currently) ─────────────────────────────────
  const filtered = useMemo(() => rawRecords, [rawRecords]);

  // ── Summary stats ────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const pmshriRecs     = filtered.filter(r => r.isPMShree === 1);
    const nonPmshriRecs  = filtered.filter(r => r.isPMShree !== 1);
    const forRegionalAll = filtered.filter(r => r.forRegional);
    const forNationalAll = filtered.filter(r => r.forNational);
    return {
      totalFacilities:  filtered.length,
      totalQuantity:    filtered.reduce((s, r) => s + r.quantity, 0),
      pmshri:           pmshriRecs.length,
      nonPmshri:        nonPmshriRecs.length,
      forRegional:      forRegionalAll.length,
      forNational:      forNationalAll.length,
    };
  }, [filtered]);

  // ── Aggregation helper ───────────────────────────────────────────────────────
  const aggregate = (groupKey: (r: RawSportRecord) => string): AggRow[] => {
    const map = new Map<string, { pmshri: number; nonPmshri: number }>();
    filtered.forEach(r => {
      const key = groupKey(r);
      if (!key) return; // skip unmapped
      const cur = map.get(key) ?? { pmshri: 0, nonPmshri: 0 };
      if (r.isPMShree === 1) cur.pmshri++; else cur.nonPmshri++;
      map.set(key, cur);
    });
    let rows: AggRow[] = Array.from(map.entries()).map(([cat, v]) => ({
      category: cat,
      pmshriCount: v.pmshri,
      nonPmshriCount: v.nonPmshri,
      total: v.pmshri + v.nonPmshri,
      pmshriPercentage: (v.pmshri + v.nonPmshri) > 0 ? (v.pmshri / (v.pmshri + v.nonPmshri)) * 100 : 0,
    }));
    // sort
    rows.sort((a, b) => {
      const aVal = sortCol === 'total' ? a.total : sortCol === 'pmshri' ? a.pmshriCount : a.nonPmshriCount;
      const bVal = sortCol === 'total' ? b.total : sortCol === 'pmshri' ? b.pmshriCount : b.nonPmshriCount;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return rows;
  };

  const getComparisonData = (): AggRow[] => {
    if (activeTab === 'regional') {
      // For regional tab, aggregate by region (RO)
      if (!regionalData?.schools) return [];
      const map = new Map<string, { pmshri: number; nonPmshri: number }>();
      regionalData.schools.forEach(school => {
        const key = school.roName;
        if (!key) return;
        const cur = map.get(key) ?? { pmshri: 0, nonPmshri: 0 };
        if (school.isPMShree === 1) cur.pmshri += school.totalRegionalGrounds;
        else cur.nonPmshri += school.totalRegionalGrounds;
        map.set(key, cur);
      });
      let rows: AggRow[] = Array.from(map.entries()).map(([cat, v]) => ({
        category: cat,
        pmshriCount: v.pmshri,
        nonPmshriCount: v.nonPmshri,
        total: v.pmshri + v.nonPmshri,
        pmshriPercentage: (v.pmshri + v.nonPmshri) > 0 ? (v.pmshri / (v.pmshri + v.nonPmshri)) * 100 : 0,
      }));
      // sort
      rows.sort((a, b) => {
        const aVal = sortCol === 'total' ? a.total : sortCol === 'pmshri' ? a.pmshriCount : a.nonPmshriCount;
        const bVal = sortCol === 'total' ? b.total : sortCol === 'pmshri' ? b.pmshriCount : b.nonPmshriCount;
        return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
      });
      return rows;
    }
    
    if (activeTab === 'national') {
      // For national tab, aggregate by region (RO)
      if (!nationalData?.schools) return [];
      const map = new Map<string, { pmshri: number; nonPmshri: number }>();
      nationalData.schools.forEach(school => {
        const key = school.roName;
        if (!key) return;
        const cur = map.get(key) ?? { pmshri: 0, nonPmshri: 0 };
        if (school.isPMShree === 1) cur.pmshri += school.totalNationalGrounds;
        else cur.nonPmshri += school.totalNationalGrounds;
        map.set(key, cur);
      });
      let rows: AggRow[] = Array.from(map.entries()).map(([cat, v]) => ({
        category: cat,
        pmshriCount: v.pmshri,
        nonPmshriCount: v.nonPmshri,
        total: v.pmshri + v.nonPmshri,
        pmshriPercentage: (v.pmshri + v.nonPmshri) > 0 ? (v.pmshri / (v.pmshri + v.nonPmshri)) * 100 : 0,
      }));
      // sort
      rows.sort((a, b) => {
        const aVal = sortCol === 'total' ? a.total : sortCol === 'pmshri' ? a.pmshriCount : a.nonPmshriCount;
        const bVal = sortCol === 'total' ? b.total : sortCol === 'pmshri' ? b.pmshriCount : b.nonPmshriCount;
        return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
      });
      return rows;
    }
    
    switch (activeTab) {
      case 'sportName':
        return aggregate(r => LOOKUP.SPORT_TITLE[r.sportType as keyof typeof LOOKUP.SPORT_TITLE] || `Sport ${r.sportType}`);
      case 'category':
        return aggregate(r => LOOKUP.SPORT_TYPE[r.type as keyof typeof LOOKUP.SPORT_TYPE] || r.type);
      case 'quality':
        return aggregate(r => LOOKUP.SPORT_QUALITY[r.quality as keyof typeof LOOKUP.SPORT_QUALITY] || r.quality);
      default:
        return [];
    }
  };


  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortCol }) =>
    sortCol === col
      ? sortDir === 'desc' ? <ChevronDown className="w-3 h-3 inline ml-1" /> : <ChevronUp className="w-3 h-3 inline ml-1" />
      : null;

  // ── Loading / Error states ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4"><Breadcrumb /></div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading sports infrastructure data...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="w-full max-w-7xl mx-auto px-4 pt-4"><Breadcrumb /></div>
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={fetchData} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 mx-auto">
              <RefreshCw className="w-4 h-4" /> Retry
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const compData = getComparisonData();

  const tabs = [
    { id: 'sportName', label: 'By Sport' },
    { id: 'category',  label: 'By Indoor/Outdoor' },
    { id: 'quality',   label: 'By Quality' },
    { id: 'regional',  label: 'Regional Grounds' },
    { id: 'national',  label: 'National Grounds' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <div className="w-full max-w-7xl mx-auto px-4 pt-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">

        {/* ── Page Header ───────────────────────────────────────────────── */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#6A1786] mb-2 flex items-center gap-2">
              <Trophy className="w-7 h-7" />
              Sports Infrastructure Analysis
            </h1>
            <p className="text-gray-600">
              Sport-wise facilities, indoor/outdoor breakdown, quality & competition readiness
            </p>
          </div>
          <button
            onClick={() => navigate('/kvs-samagam/analytics')}
            className="mt-2 md:mt-0 flex items-center gap-2 px-4 py-2 border border-purple-700 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Analytics
          </button>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────────────── */}
        {activeTab === 'regional' && regionalData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Grounds"   value={regionalData.summary.totalGrounds.toLocaleString()}   icon={<Target className="w-5 h-5" />}   color="blue"    subtitle="Regional grounds" />
            <StatCard title="PM-SHRI"         value={regionalData.summary.pmshri.totalGrounds.toLocaleString()}          icon={<Award className="w-5 h-5" />}    color="green"   subtitle="PM-SHRI schools" />
            <StatCard title="Non PM-SHRI"     value={regionalData.summary.nonPmshri.totalGrounds.toLocaleString()}       icon={<Award className="w-5 h-5" />}    color="orange"  subtitle="Other schools" />
            <StatCard title="Total Schools"   value={regionalData.schools.length.toLocaleString()}     icon={<Users className="w-5 h-5" />} color="purple"  subtitle="Schools with grounds" />
          </div>
        ) : activeTab === 'national' && nationalData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Grounds"   value={nationalData.summary.totalGrounds.toLocaleString()}   icon={<Target className="w-5 h-5" />}   color="blue"    subtitle="National grounds" />
            <StatCard title="PM-SHRI"         value={nationalData.summary.pmshri.totalGrounds.toLocaleString()}          icon={<Award className="w-5 h-5" />}    color="green"   subtitle="PM-SHRI schools" />
            <StatCard title="Non PM-SHRI"     value={nationalData.summary.nonPmshri.totalGrounds.toLocaleString()}       icon={<Award className="w-5 h-5" />}    color="orange"  subtitle="Other schools" />
            <StatCard title="Total Schools"   value={nationalData.schools.length.toLocaleString()}     icon={<Users className="w-5 h-5" />} color="purple"  subtitle="Schools with grounds" />
          </div>
        ) : activeTab === 'category' && indoorOutdoorData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Sports"     value={indoorOutdoorData.summary.totalSports.toLocaleString()}       icon={<Trophy className="w-5 h-5" />}    color="purple"  subtitle="Sports tracked" />
            <StatCard title="Total Schools"    value={indoorOutdoorData.summary.totalSchools.toLocaleString()}      icon={<Users className="w-5 h-5" />}     color="blue"    subtitle="All schools" />
            <StatCard title="PM-SHRI Schools"  value={indoorOutdoorData.summary.totalPmShriSchools.toLocaleString()} icon={<Award className="w-5 h-5" />}   color="green"   subtitle="PM-SHRI schools" />
            <StatCard title="Indoor Grounds"   value={indoorOutdoorData.summary.indoor.pmshri + indoorOutdoorData.summary.indoor.nonpmshri} icon={<Target className="w-5 h-5" />}   color="teal"    subtitle="Indoor facilities" />
            <StatCard title="Outdoor Grounds"  value={indoorOutdoorData.summary.outdoor.pmshri + indoorOutdoorData.summary.outdoor.nonpmshri} icon={<Dumbbell className="w-5 h-5" />} color="orange"  subtitle="Outdoor facilities" />
          </div>
        ) : activeTab === 'sportName' && sportsByCategoryData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Sports"     value={sportsByCategoryData.summary.totalSports.toLocaleString()}     icon={<Trophy className="w-5 h-5" />}    color="purple"  subtitle="Sports tracked" />
            <StatCard title="Total Grounds"    value={sportsByCategoryData.summary.totalGrounds.toLocaleString()}    icon={<Target className="w-5 h-5" />}   color="blue"    subtitle="All sports grounds" />
            <StatCard title="PM-SHRI"          value={sportsByCategoryData.summary.totalPmShri.toLocaleString()}     icon={<Award className="w-5 h-5" />}    color="green"   subtitle="PM-SHRI grounds" />
            <StatCard title="Non PM-SHRI"      value={sportsByCategoryData.summary.totalNonPmShri.toLocaleString()}  icon={<Award className="w-5 h-5" />}    color="orange"  subtitle="Non PM-SHRI grounds" />
            <StatCard title="Categories"       value={sportsByCategoryData.summary.totalCategories.toLocaleString()} icon={<Dumbbell className="w-5 h-5" />} color="teal"    subtitle="Sport categories" />
          </div>
        ) : activeTab === 'quality' && qualityData ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Schools"    value={qualityData.summary.totalSchools.toLocaleString()}         icon={<Users className="w-5 h-5" />}    color="purple"  subtitle="All schools" />
            <StatCard title="PM-SHRI Schools"  value={qualityData.summary.totalPmShriSchools.toLocaleString()}   icon={<Award className="w-5 h-5" />}    color="green"   subtitle="PM-SHRI schools" />
            <StatCard title="Non PM-SHRI"      value={qualityData.summary.totalNonPmShriSchools.toLocaleString()} icon={<Award className="w-5 h-5" />}  color="orange"  subtitle="Other schools" />
            <StatCard title="Indoor Grounds"   value={qualityData.summary.builtTypeStats.indoor.toLocaleString()} icon={<Target className="w-5 h-5" />}  color="teal"    subtitle="Indoor facilities" />
            <StatCard title="Outdoor Grounds"  value={qualityData.summary.builtTypeStats.outdoor.toLocaleString()} icon={<Dumbbell className="w-5 h-5" />} color="blue"  subtitle="Outdoor facilities" />
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <StatCard title="Total Quantity"   value={summary.totalQuantity.toLocaleString()}   icon={<Target className="w-5 h-5" />}   color="blue"    subtitle="Units across KVs" />
            <StatCard title="PM-SHRI"          value={summary.pmshri.toLocaleString()}          icon={<Award className="w-5 h-5" />}    color="green"   subtitle="PM-SHRI schools" />
            <StatCard title="Non PM-SHRI"      value={summary.nonPmshri.toLocaleString()}       icon={<Award className="w-5 h-5" />}    color="orange"  subtitle="Other schools" />
            <StatCard title="For Regional"     value={regionalData?.summary?.totalGrounds?.toLocaleString() || '0'}     icon={<TrendingUp className="w-5 h-5" />} color="teal"  subtitle="Regional-level ready" />
            <StatCard title="For National"     value={summary.forNational.toLocaleString()}     icon={<Dumbbell className="w-5 h-5" />} color="orange"  subtitle="National-level ready" />
          </div>
        )}


        {/* ── Chart Tabs ────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 mb-6">
          <nav className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-purple-700 border-purple-600 bg-purple-50'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Charts / Category Sections ────────────────────────────────────────────────────── */}
        {activeTab === 'sportName' && sportsByCategoryData ? (
          // Display charts and category sections for By Sport tab
          <div className="space-y-6 mb-6">
            {/* Standard Category */}
            {sportsByCategoryData.data.Standard && sportsByCategoryData.data.Standard.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Award className="w-5 h-5 text-green-600" />
                    Standard Category Sports ({sportsByCategoryData.data.Standard.length} sports)
                  </h3>
                </div>
                
                {/* Standard Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <ComparisonBarChart
                      data={sportsByCategoryData.data.Standard.slice()
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.pmShriGrounds,
                          nonPmshriCount: sport.nonPmShriGrounds,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.pmShriGrounds / sport.totalGrounds) * 100)
                        }))
                        .sort((a, b) => b.total - a.total)
                        .slice(0, 10)
                      }
                      title="Standard Category — Top 10 Sports"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={sportsByCategoryData.data.Standard.slice()
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 8)
                      }
                      title="Standard Category — Top 8 Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport Name</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-green-600">PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Non PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">PM-SHRI %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sportsByCategoryData.data.Standard.map(sport => (
                        <tr key={sport.sportId} className="hover:bg-gray-50">
                          <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                          <td className="py-2 px-4 text-sm text-center text-green-600 font-medium">{sport.pmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-blue-600 font-medium">{sport.nonPmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {((sport.pmShriGrounds / sport.totalGrounds) * 100).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ordinary Category */}
            {sportsByCategoryData.data.Ordinary && sportsByCategoryData.data.Ordinary.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-sky-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-blue-600" />
                    Ordinary Category Sports ({sportsByCategoryData.data.Ordinary.length} sports)
                  </h3>
                </div>
                
                {/* Ordinary Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <ComparisonBarChart
                      data={sportsByCategoryData.data.Ordinary.slice()
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.pmShriGrounds,
                          nonPmshriCount: sport.nonPmShriGrounds,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.pmShriGrounds / sport.totalGrounds) * 100)
                        }))
                        .sort((a, b) => b.total - a.total)
                        .slice(0, 10)
                      }
                      title="Ordinary Category — Top 10 Sports"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={sportsByCategoryData.data.Ordinary.slice()
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 8)
                      }
                      title="Ordinary Category — Top 8 Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport Name</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-green-600">PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Non PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">PM-SHRI %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sportsByCategoryData.data.Ordinary.map(sport => (
                        <tr key={sport.sportId} className="hover:bg-gray-50">
                          <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                          <td className="py-2 px-4 text-sm text-center text-green-600 font-medium">{sport.pmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-blue-600 font-medium">{sport.nonPmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {((sport.pmShriGrounds / sport.totalGrounds) * 100).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Other Category */}
            {sportsByCategoryData.data.Other && sportsByCategoryData.data.Other.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Other Sports ({sportsByCategoryData.data.Other.length} sports)
                  </h3>
                </div>
                
                {/* Other Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <ComparisonBarChart
                      data={sportsByCategoryData.data.Other.slice()
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.pmShriGrounds,
                          nonPmshriCount: sport.nonPmShriGrounds,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.pmShriGrounds / sport.totalGrounds) * 100)
                        }))
                        .sort((a, b) => b.total - a.total)
                        .slice(0, 10)
                      }
                      title="Other Category — Top 10 Sports"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={sportsByCategoryData.data.Other.slice()
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))
                        .sort((a, b) => b.value - a.value)
                        .slice(0, 8)
                      }
                      title="Other Category — Top 8 Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport Name</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-green-600">PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Non PM-SHRI</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">PM-SHRI %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sportsByCategoryData.data.Other.map(sport => (
                        <tr key={sport.sportId} className="hover:bg-gray-50">
                          <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                          <td className="py-2 px-4 text-sm text-center text-green-600 font-medium">{sport.pmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-blue-600 font-medium">{sport.nonPmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {((sport.pmShriGrounds / sport.totalGrounds) * 100).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'sportName' ? (
          // Loading or error state for By Sport tab
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center mb-6">
            {sportsLoading ? (
              <>
                <Loader2 className="w-12 h-12 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-gray-500 text-sm">Loading sports data...</p>
              </>
            ) : sportsError ? (
              <>
                <Trophy className="w-12 h-12 mx-auto text-red-300 mb-3" />
                <p className="text-red-500 text-sm">Failed to load sports data</p>
              </>
            ) : (
              <>
                <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No sports data available</p>
              </>
            )}
          </div>
        ) : activeTab === 'category' && indoorOutdoorData ? (
          // Display indoor/outdoor data from API
          <div className="space-y-6 mb-6">
            {/* Standard Category */}
            {indoorOutdoorData.data.Standard && indoorOutdoorData.data.Standard.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Award className="w-5 h-5 text-green-600" />
                    Standard Quality Grounds
                  </h3>
                </div>
                
                {/* Standard Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ComparisonBarChart
                      data={indoorOutdoorData.data.Standard.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 10)
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.indoor.pmshri + sport.outdoor.pmshri,
                          nonPmshriCount: sport.indoor.nonpmshri + sport.outdoor.nonpmshri,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.indoor.pmshri + sport.outdoor.pmshri) / sport.totalGrounds) * 100
                        }))}
                      title="Top 10 Standard Sports — Indoor/Outdoor"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={indoorOutdoorData.data.Standard.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 8)
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))}
                      title="Standard Sports Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-600">Indoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-500">Indoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Outdoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-500">Outdoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {indoorOutdoorData.data.Standard.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .map((sport) => (
                          <tr key={sport.sportId} className="hover:bg-gray-50">
                            <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-600">{sport.indoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-500">{sport.indoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-600">{sport.outdoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-500">{sport.outdoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Ordinary Category */}
            {indoorOutdoorData.data.Ordinary && indoorOutdoorData.data.Ordinary.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-sky-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Ordinary Quality Grounds
                  </h3>
                </div>
                
                {/* Ordinary Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ComparisonBarChart
                      data={indoorOutdoorData.data.Ordinary.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 10)
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.indoor.pmshri + sport.outdoor.pmshri,
                          nonPmshriCount: sport.indoor.nonpmshri + sport.outdoor.nonpmshri,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.indoor.pmshri + sport.outdoor.pmshri) / sport.totalGrounds) * 100
                        }))}
                      title="Top 10 Ordinary Sports — Indoor/Outdoor"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={indoorOutdoorData.data.Ordinary.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 8)
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))}
                      title="Ordinary Sports Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-600">Indoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-500">Indoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Outdoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-500">Outdoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {indoorOutdoorData.data.Ordinary.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .map((sport) => (
                          <tr key={sport.sportId} className="hover:bg-gray-50">
                            <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-600">{sport.indoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-500">{sport.indoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-600">{sport.outdoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-500">{sport.outdoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Other Category */}
            {indoorOutdoorData.data.Other && indoorOutdoorData.data.Other.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-purple-600" />
                    Other Grounds
                  </h3>
                </div>
                
                {/* Other Category Charts */}
                <div className="p-6 bg-gray-50">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <ComparisonBarChart
                      data={indoorOutdoorData.data.Other.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 10)
                        .map(sport => ({
                          category: sport.sportName,
                          pmshriCount: sport.indoor.pmshri + sport.outdoor.pmshri,
                          nonPmshriCount: sport.indoor.nonpmshri + sport.outdoor.nonpmshri,
                          total: sport.totalGrounds,
                          pmshriPercentage: ((sport.indoor.pmshri + sport.outdoor.pmshri) / sport.totalGrounds) * 100
                        }))}
                      title="Top 10 Other Sports — Indoor/Outdoor"
                      xAxisLabel="Sport"
                    />
                    <DistributionPieChart
                      data={indoorOutdoorData.data.Other.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .slice(0, 8)
                        .map(sport => ({ name: sport.sportName, value: sport.totalGrounds }))}
                      title="Other Sports Distribution"
                    />
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sport</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-600">Indoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-teal-500">Indoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Outdoor (PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-blue-500">Outdoor (Non PM-SHRI)</th>
                        <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {indoorOutdoorData.data.Other.slice()
                        .sort((a, b) => b.totalGrounds - a.totalGrounds)
                        .map((sport) => (
                          <tr key={sport.sportId} className="hover:bg-gray-50">
                            <td className="py-2 px-4 text-sm text-gray-700 font-medium">{sport.sportName}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-600">{sport.indoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-teal-500">{sport.indoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-600">{sport.outdoor.pmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-blue-500">{sport.outdoor.nonpmshri.toLocaleString()}</td>
                            <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{sport.totalGrounds.toLocaleString()}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'category' ? (
          // Loading or error state for By Indoor/Outdoor tab
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center mb-6">
            {indoorOutdoorLoading ? (
              <>
                <Loader2 className="w-12 h-12 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-gray-500 text-sm">Loading indoor/outdoor data...</p>
              </>
            ) : indoorOutdoorError ? (
              <>
                <Trophy className="w-12 h-12 mx-auto text-red-300 mb-3" />
                <p className="text-red-500 text-sm">Failed to load indoor/outdoor data</p>
              </>
            ) : (
              <>
                <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No indoor/outdoor data available</p>
              </>
            )}
          </div>
        ) : activeTab === 'quality' && qualityData ? (
          // Display quality data from API
          <div className="space-y-6 mb-6">
            {/* Quality Charts */}
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ComparisonBarChart
                  data={Object.entries(qualityData.data).slice()
                    .filter(([key]) => key !== 'N/A')
                    .map(([quality, data]) => ({
                      category: quality,
                      pmshriCount: data.pmShriGrounds,
                      nonPmshriCount: data.nonPmShriGrounds,
                      total: data.totalGrounds,
                      pmshriPercentage: ((data.pmShriGrounds / data.totalGrounds) * 100)
                    }))
                    .sort((a, b) => b.total - a.total)
                  }
                  title="Quality-wise Distribution — PM-SHRI vs Non PM-SHRI"
                  xAxisLabel="Quality Type"
                />
                <DistributionPieChart
                  data={Object.entries(qualityData.data).slice()
                    .filter(([key]) => key !== 'N/A')
                    .map(([quality, data]) => ({ name: quality, value: data.totalGrounds }))
                    .sort((a, b) => b.value - a.value)
                  }
                  title="Quality Distribution"
                />
              </div>
            </div>

            {/* Quality Summary Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Award className="w-5 h-5 text-purple-600" />
                  Sports Ground Quality Analysis
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Quality Type</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-green-600">PM-SHRI</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-blue-600">Non PM-SHRI</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-purple-600">Total</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">PM-SHRI %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Object.entries(qualityData.data).slice()
                      .sort(([, a], [, b]) => b.totalGrounds - a.totalGrounds)
                      .map(([quality, data]) => (
                        <tr key={quality} className="hover:bg-gray-50">
                          <td className="py-2 px-4 text-sm text-gray-700 font-medium">
                            {quality === 'N/A' ? (
                              <span className="text-gray-400 italic">Not Specified</span>
                            ) : (
                              quality
                            )}
                          </td>
                          <td className="py-2 px-4 text-sm text-center text-green-600 font-medium">{data.pmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-blue-600 font-medium">{data.nonPmShriGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center text-purple-600 font-bold">{data.totalGrounds.toLocaleString()}</td>
                          <td className="py-2 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {((data.pmShriGrounds / data.totalGrounds) * 100).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Additional Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Quality Statistics
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Standard Quality:</span>
                    <span className="text-sm font-bold text-purple-600">{qualityData.summary.qualityStats.standard.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Ordinary Quality:</span>
                    <span className="text-sm font-bold text-blue-600">{qualityData.summary.qualityStats.ordinary.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h4 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Dumbbell className="w-5 h-5 text-teal-600" />
                  Built Type Statistics
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Indoor:</span>
                    <span className="text-sm font-bold text-green-600">{qualityData.summary.builtTypeStats.indoor.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Outdoor:</span>
                    <span className="text-sm font-bold text-orange-600">{qualityData.summary.builtTypeStats.outdoor.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'quality' ? (
          // Loading or error state for Quality tab
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center mb-6">
            {qualityLoading ? (
              <>
                <Loader2 className="w-12 h-12 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-gray-500 text-sm">Loading quality data...</p>
              </>
            ) : qualityError ? (
              <>
                <Trophy className="w-12 h-12 mx-auto text-red-300 mb-3" />
                <p className="text-red-500 text-sm">Failed to load quality data</p>
              </>
            ) : (
              <>
                <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500 text-sm">No quality data available</p>
              </>
            )}
          </div>
        ) : compData.length > 0 ? (
          // Display charts for regional and national tabs
          <div className={`grid gap-6 mb-6 ${(activeTab === 'regional' || activeTab === 'national') ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
            <ComparisonBarChart
              data={compData}
              title={`${tabs.find(t => t.id === activeTab)?.label} — PM-SHRI vs Non PM-SHRI`}
              xAxisLabel="Category"
            />
            <DistributionPieChart
              data={compData.map(d => ({ name: d.category, value: d.total }))}
              title={`${tabs.find(t => t.id === activeTab)?.label} Distribution`}
            />
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center mb-6">
            <Trophy className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">No records match the current filters.</p>
          </div>
        )}

        {/* ── Detailed Table (hidden for sportName, category, and quality tabs) ────────────────────────────────────────────── */}
        {activeTab !== 'sportName' && activeTab !== 'category' && activeTab !== 'quality' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {tabs.find(t => t.id === activeTab)?.label} — Detailed Breakdown
              </h3>
              <span className="text-xs text-gray-500">{compData.length} groups · {summary.totalFacilities} records</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Name</th>
                    <th
                      className="text-center py-3 px-4 text-sm font-semibold text-green-600 cursor-pointer select-none hover:text-green-700"
                      onClick={() => handleSort('pmshri')}
                    >
                      PM-SHRI <SortIcon col="pmshri" />
                    </th>
                    <th
                      className="text-center py-3 px-4 text-sm font-semibold text-blue-600 cursor-pointer select-none hover:text-blue-700"
                      onClick={() => handleSort('nonPmshri')}
                    >
                      Non PM-SHRI <SortIcon col="nonPmshri" />
                    </th>
                    <th
                      className="text-center py-3 px-4 text-sm font-semibold text-purple-600 cursor-pointer select-none hover:text-purple-700"
                      onClick={() => handleSort('total')}
                    >
                      Total <SortIcon col="total" />
                    </th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-gray-500">PM-SHRI %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {compData.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-sm text-gray-400">No data available</td>
                    </tr>
                  ) : (
                    <>
                      {compData.map(row => (
                        <tr key={row.category} className="hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-700 font-medium">{row.category}</td>
                          <td className="py-3 px-4 text-sm text-center text-green-600 font-medium">{row.pmshriCount.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center text-blue-600 font-medium">{row.nonPmshriCount.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center text-purple-600 font-bold">{row.total.toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {row.pmshriPercentage.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                      {compData.length > 0 && (
                        <tr className="bg-gray-50 border-t-2 border-gray-300 font-bold">
                          <td className="py-3 px-4 text-sm text-gray-800">Grand Total</td>
                          <td className="py-3 px-4 text-sm text-center text-green-700">{compData.reduce((sum, row) => sum + row.pmshriCount, 0).toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center text-blue-700">{compData.reduce((sum, row) => sum + row.nonPmshriCount, 0).toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center text-purple-700">{compData.reduce((sum, row) => sum + row.total, 0).toLocaleString()}</td>
                          <td className="py-3 px-4 text-sm text-center">
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              {compData.length > 0 ? ((compData.reduce((sum, row) => sum + row.pmshriCount, 0) / compData.reduce((sum, row) => sum + row.total, 0)) * 100).toFixed(1) : 0}%
                            </span>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Top Schools for Regional Grounds (when Regional tab is active) ──── */}
        {activeTab === 'regional' && regionalData?.schools && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-purple-600" />
                Top Schools with Regional Grounds
              </h3>
              <span className="text-xs text-gray-500">{regionalData.schools.length} schools · {regionalData.summary.totalGrounds} total grounds</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rank</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">School Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Region</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">State</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">District</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Regional Grounds</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {regionalData.schools.slice(0, 50).map((school, idx) => (
                    <tr key={school.schoolID} className="hover:bg-gray-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: { schoolID: school.schoolID, schoolName: school.schoolName, state: school.stateName, region: school.roName, district: school.districtName, totalRegionalGrounds: school.totalRegionalGrounds }, tabLabel: 'Regional Sports Grounds' })}>
                      <td className="py-2 px-4 text-sm text-gray-500 font-medium">#{idx + 1}</td>
                      <td className="py-2 px-4 text-sm">
                        <div className="font-medium text-gray-800">{school.schoolName}</div>
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.roName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.stateName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.districtName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-center">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
                          {school.totalRegionalGrounds}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-sm text-center">
                        {school.isPMShree === 1 ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">PM-SHRI</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Non PM-SHRI</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {regionalData.schools.length > 50 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
                Showing top 50 of {regionalData.schools.length} schools
              </div>
            )}
          </div>
        )}

        {/* ── Top Schools for National Grounds (when National tab is active) ──── */}
        {activeTab === 'national' && nationalData?.schools && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-purple-600" />
                Top Schools with National Grounds
              </h3>
              <span className="text-xs text-gray-500">{nationalData.schools.length} schools · {nationalData.summary.totalGrounds} total grounds</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Rank</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">School Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Region</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">State</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">District</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">National Grounds</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {nationalData.schools.slice(0, 50).map((school, idx) => (
                    <tr key={school.schoolID} className="hover:bg-gray-50 cursor-pointer" onClick={() => school.schoolID && setSelectedSchool({ schoolID: school.schoolID, schoolName: school.schoolName, infraData: { schoolID: school.schoolID, schoolName: school.schoolName, state: school.stateName, region: school.roName, district: school.districtName, totalNationalGrounds: school.totalNationalGrounds }, tabLabel: 'National Sports Grounds' })}>
                      <td className="py-2 px-4 text-sm text-gray-500 font-medium">#{idx + 1}</td>
                      <td className="py-2 px-4 text-sm">
                        <div className="font-medium text-gray-800">{school.schoolName}</div>
                      </td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.roName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.stateName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-gray-600">{school.districtName || '—'}</td>
                      <td className="py-2 px-4 text-sm text-center">
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-bold">
                          {school.totalNationalGrounds}
                        </span>
                      </td>
                      <td className="py-2 px-4 text-sm text-center">
                        {school.isPMShree === 1 ? (
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">PM-SHRI</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs">Non PM-SHRI</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {nationalData.schools.length > 50 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
                Showing top 50 of {nationalData.schools.length} schools
              </div>
            )}
          </div>
        )}

        {/* ── Top Schools Raw Table ─────────────────────────────────────── */}
        {activeTab !== 'regional' && activeTab !== 'national' && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-blue-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Facility Records (Top 50)
            </h3>
            <span className="text-xs text-gray-500">
              {topFacilityData ? topFacilityData.data.length : filtered.length} total
            </span>
          </div>
          <div className="overflow-x-auto">
            {topFacilityLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-purple-600 animate-spin mr-2" />
                <p className="text-gray-500">Loading facility records...</p>
              </div>
            ) : topFacilityError ? (
              <div className="flex items-center justify-center py-12">
                <Trophy className="w-8 h-8 text-red-400 mr-2" />
                <p className="text-red-500">Failed to load facility records</p>
              </div>
            ) : topFacilityData && topFacilityData.data.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">School</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Total Sports</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Indoor</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Outdoor</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600">Sports Available</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {topFacilityData.data.slice(0, 50).map((record: any, idx: number) => {
                    const sportsArray = record.facilities?.sportsAvailable 
                      ? record.facilities.sportsAvailable.split(', ').slice(0, 3) 
                      : [];
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-2 px-4 text-sm">
                          <div className="font-medium text-gray-800">{record.schoolName || '—'}</div>
                          <div className="text-xs text-gray-500">{record.stateName || ''} {record.roName ? `· ${record.roName}` : ''}</div>
                        </td>
                        <td className="py-2 px-4 text-sm text-center font-semibold text-gray-800">
                          {record.facilities?.totalSportFacilities || '0'}
                        </td>
                        <td className="py-2 px-4 text-sm text-center">
                          <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs font-medium">
                            {record.facilities?.indoorFacilities || '0'}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm text-center">
                          <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs font-medium">
                            {record.facilities?.outdoorFacilities || '0'}
                          </span>
                        </td>
                        <td className="py-2 px-4 text-sm text-gray-700 cursor-help" title={record.facilities?.sportsAvailable || 'N/A'}>
                          <div className="truncate text-xs">
                            {sportsArray.length > 0 ? (
                              <>
                                {sportsArray.join(', ')}
                                {record.facilities?.sportsAvailable && record.facilities.sportsAvailable.split(', ').length > 3 && (
                                  <span className="text-gray-500"> +{record.facilities.sportsAvailable.split(', ').length - 3} more</span>
                                )}
                              </>
                            ) : (
                              '—'
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4 text-sm text-center">
                          {record.isPMShree
                            ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">PM-SHRI</span>
                            : <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">Non PM-SHRI</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center py-12">
                <Trophy className="w-8 h-8 text-gray-300 mr-2" />
                <p className="text-gray-500">No facility records available</p>
              </div>
            )}
          </div>
          {topFacilityData && topFacilityData.data.length > 50 && (
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
              Showing top 50 of {topFacilityData.data.length} facility records
            </div>
          )}
        </div>
        )}

        {/* ── Detailed Analysis Reports ─────────────────────────────────── */}
        <div className="mt-4 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-[#6A1786] mb-4">Detailed Analysis Reports</h3>
          <p className="text-sm text-gray-600 mb-4">Access comprehensive analysis reports for each category</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { name: 'Infrastructure',  path: '/kvs-samagam/analytics/infrastructure',    icon: '🏫', color: 'blue' },
              { name: 'Enrollment',      path: '/kvs-samagam/analytics/enrollment',        icon: '👨‍🎓', color: 'green' },
              { name: 'ICT Profile',     path: '/kvs-samagam/analytics/ict-profile',       icon: '💻', color: 'purple' },
              { name: 'Library Profile', path: '/kvs-samagam/analytics/library-profile',   icon: '📚', color: 'orange' },
              { name: 'CWSN Profile',    path: '/kvs-samagam/analytics/cwsn-profile',      icon: '♿', color: 'pink' },
              { name: 'NCC Profile',     path: '/kvs-samagam/analytics/ncc-profile',       icon: '🎖️', color: 'cyan' },
              { name: 'Sports',          path: '/kvs-samagam/analytics/sports',            icon: '🏆', color: 'yellow' },
              { name: 'Green Building',  path: '/kvs-samagam/analytics/green-building',    icon: '🌿', color: 'teal' },
              { name: 'Safety',          path: '/kvs-samagam/analytics/safety',            icon: '🛡️', color: 'red' },
              { name: 'Special Labs',    path: '/kvs-samagam/analytics/special-labs',      icon: '🔬', color: 'indigo' },
              { name: 'Water Profile',   path: '/kvs-samagam/analytics/miscellaneous',      icon: '💧', color: 'teal' },
            ].map(item => {
              const isCurrentPage = item.name === 'Sports';
              return (
                <button
                  key={item.name}
                  onClick={() => !isCurrentPage && navigate(item.path)}
                  disabled={isCurrentPage}
                  className={`bg-gradient-to-br from-${item.color}-50 to-${item.color}-100 rounded-lg p-4 border border-${item.color}-200 text-center transition-all ${
                    isCurrentPage ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer hover:scale-105'
                  }`}
                >
                  <span className="text-3xl">{item.icon}</span>
                  <p className="text-sm font-medium text-gray-700 mt-2">{item.name}</p>
                </button>
              );
            })}
          </div>
        </div>

      </main>
      <Footer />

      {selectedSchool && (
        <SchoolDetailsModal
          schoolID={selectedSchool.schoolID}
          onClose={() => setSelectedSchool(null)}
          schoolKPIData={selectedSchool.infraData}
          selectedCategory={selectedSchool.tabLabel}
        />
      )}
    </div>
  );
}
